/** @jsxImportSource @emotion/react */

import { LayerControlButtons } from '../LayerControlButtons'
import { Box, Typography, useTheme } from '@mui/material'
import { accordionHazards } from '..'
import {
  accordionHazardContentContainer,
  feedEmptyBorder,
  feedErrorBorder,
  hiddenLayerContainer,
  hiddenLayerLabel,
  layerHeader,
} from './LayerAccordionList.styles'
import { MapLayer, MapLayerType } from '@app/redux/riskMap/riskMapSlice'
import { DialogLayersPreviewProps } from '@app/components/Molecules/MapView/RiskMapView/AddLayersDialog/LayersPreview'
import { Accordion } from '@app/components/Atoms/Accordion'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SortableList } from '@app/components/Atoms/Sortable'
import { usePreferences } from '@app/hooks/usePreferences'
import { LayerDetailsReport } from '@app/components/Molecules/RiskSideDrawerContent/data_fetchers/layerDetailsFetcher'
import { LayerInfoAccordions } from '../../../../InfoAccordions'
import { useLiveContext } from '@app/modules/Live/contexts/Live.Context'
import { Icon } from '@app/components/Atoms/Icon'

export const LayerAccordionList = function LayerAccordList({
  layers,
  layerDetailsList,
  handleUpdateLayer,
  dialogProps,
  isDialogPreview,
  handleDeleteLayer,
}: {
  layers: MapLayer[]
  layerDetailsList: LayerDetailsReport[][] | (LayerDetailsReport[][] | null)[] | null
  handleUpdateLayer: ({
    parentLayerId,
    newLayers,
  }: {
    parentLayerId: MapLayerType
    newLayers: MapLayer[]
  }) => void
  dialogProps?: DialogLayersPreviewProps
  isDialogPreview: boolean
  handleDeleteLayer: (props: { selectedLayer: MapLayer }) => void
}) {
  const theme = useTheme()
  const { use_hazard_name } = usePreferences()
  const { liveLayers, liveData } = useLiveContext()
  const liveLayerErrorIds = useMemo(
    () => new Set(liveLayers.filter((l) => l.isError).map((l) => l.id)),
    [liveLayers],
  )
  const emptyLiveLayerIds = useMemo(
    () =>
      new Set(
        liveLayers
          .filter((l) => {
            const data = liveData[l.id]
            return data !== null && data?.features.length === 0
          })
          .map((l) => l.id),
      ),
    [liveLayers, liveData],
  )
  const parentLayerId = useMemo(() => layers[0].layerType, [layers])
  const triggeredUpdate = useRef(false)

  const [items, setItems] = useState<MapLayer[]>([])

  useEffect(() => {
    if (triggeredUpdate.current) {
      triggeredUpdate.current = false
      return
    }
    setItems(
      layers.map((layer) => {
        return { ...layer, id: layer.id ?? layer.tilesets.map((tileset) => tileset.id).join('-') }
      }),
    )
  }, [layers])

  return (
    <>
      <SortableList
        items={items}
        onChange={(newLayers) => {
          triggeredUpdate.current = true
          setItems(newLayers)
          handleUpdateLayer({ parentLayerId, newLayers })
        }}
        renderItem={(layer, _, _isDragging) => {
          const layerDetails = layerDetailsList
            ? (layerDetailsList.find(
                (detailReport) =>
                  Array.isArray(detailReport) &&
                  detailReport.some(
                    (detail) =>
                      !Array.isArray(detail) &&
                      (detail.layer_id === layer.type ||
                        detail.layer_id === layer.assetTag ||
                        detail.layer_id === layer.id.replace('urbanintelligence.', '')),
                  ),
              ) as LayerDetailsReport[] | null)
            : null

          const canExpand =
            (layerDetails && layerDetails.length > 0 && !isDialogPreview) ||
            (!!layer.filterable_information && !isDialogPreview && !!layerDetails) ||
            false

          const isFeedUnavailable = liveLayerErrorIds.has(layer.id)
          const isFeedEmpty = !isFeedUnavailable && emptyLiveLayerIds.has(layer.id)

          return (
            <SortableList.Item key={layer.id} id={layer.id}>
              <Accordion
                css={accordionHazards({ theme })}
                customStyle={
                  isFeedUnavailable
                    ? feedErrorBorder({ theme })
                    : isFeedEmpty
                      ? feedEmptyBorder({ theme })
                      : undefined
                }
                defaultExpanded={false}
                lazyContent={false}
                variant={'outline'}
                level={'h3'}
                isDraggable
                accordionStateKey={canExpand ? `layer-accordion-list-${layer.id}` : undefined}
                header={
                  <Box css={layerHeader({ canExpand })}>
                    <Box css={hiddenLayerContainer({ theme })}>
                      {isFeedUnavailable && (
                        <Icon
                          iconName="NotInterested"
                          colour={'rgb(244,67,54) !important'}
                          size="small"
                        />
                      )}
                      {isFeedEmpty && (
                        <Icon iconName="Info" colour={'#1A3E5D !important'} size="small" />
                      )}
                      <Typography
                        css={hiddenLayerLabel({
                          theme,
                          isVisible: layer.visible,
                        })}
                      >
                        {use_hazard_name.flag
                          ? (layer.hazard_name ?? layer.display_name)
                          : layer.display_name}
                      </Typography>
                    </Box>

                    <LayerControlButtons
                      layer={layer}
                      layerDetails={layerDetails}
                      dialogProps={dialogProps}
                      isDialogPreview={isDialogPreview}
                      handleDeleteLayer={handleDeleteLayer}
                    />
                  </Box>
                }
                body={
                  canExpand ? (
                    <Box css={accordionHazardContentContainer({ theme })}>
                      <Box key={layer.id}>
                        <LayerInfoAccordions
                          layer={layer}
                          layerType={layer.layerType}
                          layerDetails={layerDetails}
                        />
                      </Box>
                    </Box>
                  ) : undefined
                }
              />
            </SortableList.Item>
          )
        }}
      />
    </>
  )
}
